import React, { useMemo } from "react";
import { X, CheckCircle2, Circle } from "lucide-react";
import { BLOCKS } from "../../data/studioConfigSchema";

/**
 * StudioInlineDrawer — fallback right-side drawer that exposes the FULL 8-module
 * tunnel so the pro can complete remaining questions without leaving the sandbox.
 *
 * Already-answered questions are pre-filled and highlighted; the user can edit
 * them inline. New answers trigger the same parent `onAnswer(blockId, qId, value)`
 * callback (which debounces the server PATCH).
 */
export default function StudioInlineDrawer({ open, onClose, answers, onAnswer }) {
  const a = answers || {};

  // Compute answered count per block for the section badges.
  const stats = useMemo(() => {
    const out = {};
    for (const [blockId, block] of Object.entries(BLOCKS)) {
      const totalB = block.questions.length;
      let answeredB = 0;
      for (const q of block.questions) {
        const v = (a[blockId] || {})[q.id];
        if (v === undefined || v === null || v === "") continue;
        if (Array.isArray(v) && v.length === 0) continue;
        answeredB += 1;
      }
      out[blockId] = { answered: answeredB, total: totalB };
    }
    return out;
  }, [a]);

  if (!open) return null;

  return (
    <>
      {/* Scrim */}
      <div
        className="fixed inset-0 bg-black/55 z-[60] backdrop-blur-sm"
        onClick={onClose}
        data-testid="drawer-scrim"
      />
      {/* Panel */}
      <aside
        className="fixed right-0 top-0 bottom-0 w-full sm:w-[480px] lg:w-[560px] z-[61] shadow-2xl flex flex-col"
        style={{ background: "#ffffff", borderLeft: "0.5px solid #E8EDF5" }}
        data-testid="studio-inline-drawer"
        role="dialog"
        aria-label="Toutes les questions de configuration"
      >
        <header
          className="px-6 py-5 flex items-center justify-between shrink-0"
          style={{ background: "#0F2B5B" }}
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: "#93B4D4" }}>Fallback</div>
            <h2 className="font-display text-[18px] leading-tight text-white">Toutes les questions</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="drawer-close"
            className="grid place-items-center h-9 w-9 rounded-full transition"
            style={{ color: "#93B4D4" }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "#ffffff"; e.currentTarget.style.background = "rgba(255,255,255,0.1)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "#93B4D4"; e.currentTarget.style.background = "transparent"; }}
          >
            <X size={18} />
          </button>
        </header>

        <p className="px-6 pt-4 text-[12px] leading-relaxed" style={{ color: "#64748B" }}>
          Vous pouvez répondre ici aux questions restantes — celles déjà complétées via les bandeaux apparaissent pré-cochées. Tout est enregistré automatiquement.
        </p>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="drawer-blocks" style={{ background: "#ffffff" }}>
          {Object.entries(BLOCKS).map(([blockId, block]) => {
            const Icon = block.icon;
            const s = stats[blockId];
            const isComplete = s.answered === s.total;
            return (
              <section
                key={blockId}
                className="rounded-lg p-4"
                style={{ background: "#F0F9FF", borderLeft: "3px solid #4FC3F7", borderRadius: "0 8px 8px 0" }}
                data-testid={`drawer-block-${blockId}`}
              >
                <header className="flex items-start gap-3 mb-3">
                  <span
                    className="grid place-items-center h-8 w-8 rounded-lg shrink-0"
                    style={{ background: "#0F2B5B", color: "#4FC3F7" }}
                  >
                    <Icon size={15} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-[14px] font-semibold leading-tight" style={{ color: "#0F2B5B" }}>{block.title}</h3>
                      {block.new && (
                        <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded font-bold" style={{ background: "#4FC3F7", color: "#0F2B5B" }}>Nouveau</span>
                      )}
                      <span
                        className="text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 font-semibold"
                        style={{
                          background: "#0F2B5B",
                          color: "#ffffff",
                        }}
                      >
                        {isComplete ? <CheckCircle2 size={10} /> : <Circle size={10} />}
                        {s.answered}/{s.total}
                      </span>
                    </div>
                    <p className="text-[11.5px] mt-0.5 leading-snug" style={{ color: "#64748B" }}>{block.summary}</p>
                  </div>
                </header>

                <div className="space-y-3 pl-1">
                  {block.questions.map((q) => (
                    <DrawerQuestion
                      key={q.id}
                      q={q}
                      value={(a[blockId] || {})[q.id]}
                      onChange={(value) => onAnswer(blockId, q.id, value)}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </aside>
    </>
  );
}

function DrawerQuestion({ q, value, onChange }) {
  const answered =
    value !== undefined &&
    value !== null &&
    value !== "" &&
    !(Array.isArray(value) && value.length === 0);

  return (
    <div data-testid={`drawer-q-${q.id}`}>
      <div className="flex items-baseline gap-1.5 mb-1.5">
        {answered ? (
          <CheckCircle2 size={11} className="text-emerald-500 shrink-0 translate-y-0.5" />
        ) : (
          <Circle size={11} className="text-slate-300 shrink-0 translate-y-0.5" />
        )}
        <div className="flex-1">
          <div className={`text-[12.5px] leading-snug font-medium ${answered ? "text-slate-500" : "text-slate-800"}`}>
            {q.label}
            {q.optional && <span className="text-slate-400 text-[10.5px] ml-1.5">(facultatif)</span>}
          </div>
          {q.helper && <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{q.helper}</p>}
        </div>
      </div>

      <div className="pl-5">
        {q.type === "radio" && (
          <div className="flex flex-wrap gap-1.5">
            {q.options.map((opt) => {
              const active = value === opt;
              return (
                <button
                  key={opt}
                  onClick={() => onChange(active ? "" : opt)}
                  className={`text-[11.5px] px-2.5 py-1 rounded-full border transition ${
                    active
                      ? "bg-[#0F2B5B] text-white border-[#0F2B5B] font-semibold"
                      : "bg-white text-slate-600 border-slate-200 hover:border-[#0F2B5B] hover:text-[#0F2B5B]"
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        )}

        {q.type === "checkbox" && (
          <div className="flex flex-wrap gap-1.5">
            {q.options.map((opt) => {
              const arr = Array.isArray(value) ? value : [];
              const active = arr.includes(opt);
              return (
                <button
                  key={opt}
                  onClick={() => {
                    const next = active ? arr.filter((x) => x !== opt) : [...arr, opt];
                    onChange(next);
                  }}
                  className={`text-[11.5px] px-2.5 py-1 rounded-full border transition ${
                    active
                      ? "bg-[#0F2B5B] text-white border-[#0F2B5B] font-semibold"
                      : "bg-white text-slate-600 border-slate-200 hover:border-[#0F2B5B] hover:text-[#0F2B5B]"
                  }`}
                >
                  {active ? "✓ " : "+ "}{opt}
                </button>
              );
            })}
          </div>
        )}

        {(q.type === "text" || q.type === "textarea") && (
          q.type === "textarea" ? (
            <textarea
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              rows={2}
              className="w-full text-[12px] px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-[#0F2B5B]"
              placeholder={q.label}
            />
          ) : (
            <input
              type="text"
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              className="w-full text-[12px] px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-[#0F2B5B]"
              placeholder={q.label}
            />
          )
        )}
      </div>
    </div>
  );
}
